import { Badge, Switch, Tag } from 'antd';
import type { BadgeProps } from 'antd';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { currencySelector } from 'app/redux/selectors';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';
import { format as formatDate } from 'app/utils/dayjs';
import { formatCurrency } from 'app/utils/strings';

import 'styles/lists/cells.scss';

import { NoPicture } from '../commons/NoPicture/NoPicture';
import { PictureComponent } from '../commons/PictureComponent/PictureComponent';
import type { PictureComponentProps } from '../commons/PictureComponent/PictureComponent';
import { PictureComponentV2 } from '../commons/PictureComponentV2/PictureComponentV2';

export const dateTimeCell =
  (format: string) => (date: string | number | Date) =>
    date && formatDate(new Date(date), format);

export const dateTimeCell2 = (date: string | number | Date) =>
  date && (
    <div style={{ minWidth: 138 }}>
      {formatDate(new Date(date), 'DD/MM/YYYY [at] HH:mm')}
    </div>
  );

export const lengthCell = () => (array: unknown[] | null | undefined) =>
  array ? array.length : '0';

export const entityCell =
  (entities: { id: unknown; name: unknown }[]) => (entityId: unknown) =>
    entities.find((entity) => entity.id === entityId)?.name ?? 'Unknown';

export const percentCell = (ratio: number) => `${Math.round(100 * ratio)}%`;

type PriceCellProps = {
  price: number;
  entity: unknown;
  defaultText?: string;
};

export const PriceCell = ({
  price,
  entity,
  defaultText = '',
}: PriceCellProps) => {
  const currency = useSelector(currencySelector(entity));

  return <>{formatCurrency(price, currency, defaultText)}</>;
};

export const boolTextCell =
  (textTrue: string, textFalse: string) => (bool: boolean) =>
    bool ? textTrue : textFalse;

export const SwitchCell =
  (onChange: (id: number, value: boolean) => void) =>
  (featured: boolean, entity: { id: number }) =>
    (
      <Switch
        checked={featured}
        onChange={() => onChange(entity.id, !featured)}
        size="small"
      />
    );

export const BadgeCell =
  (badges: (BadgeProps & { value: unknown })[]) => (value: unknown) => {
    const badgeData = badges?.find((badge) => badge.value === value);

    return badgeData && <Badge {...badgeData} />;
  };

export const PublishedCell = (
  published: boolean,
  { hidden }: { hidden?: boolean; [key: string]: unknown }
) =>
  hidden ? (
    <Badge color="#bfbfbf" text="Hidden" />
  ) : published ? (
    <Badge status="success" text="Published" />
  ) : (
    <Badge status="warning" text="Unpublished" />
  );

export const NewCell = (isNew: boolean) =>
  isNew ? <Tag color="#FF3366">NEW</Tag> : '';

export const StatusCell = (status: string) => {
  switch (status) {
    case 'accepted':
      return (
        <Badge style={{ marginRight: 0 }} status="success" text="Confirmed" />
      ); // GRENN-6
    case 'canceled':
    case 'cancelled':
      return (
        <Badge style={{ marginRight: 0 }} status="error" text="Canceled" />
      ); // ORANGE-6
    case 'pending':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Pending" />
      ); // GRAY-6
    case 'expired':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Expired" />
      ); // GRAY-6
    case 'aborted':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Aborted" />
      ); // GRAY-6
    case 'rejected':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Rejected" />
      ); // RED-6

    default:
      return status;
  }
};

export const StatusGiftCardBadge = (type: string) => {
  switch (type) {
    case 'active':
      return (
        <Badge style={{ marginRight: 0 }} status="success" text="Active" />
      ); // GRENN-6
    case 'expired':
      return (
        <Badge style={{ marginRight: 0 }} status="warning" text="Expired" />
      ); // ORANGE-6
    case 'empty':
      return <Badge style={{ marginRight: 0 }} status="default" text="Empty" />; // GRAY-6
    case 'rejected':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Rejected" />
      ); // GRAY-6
    case 'pending':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Pending" />
      ); // GRAY-6
    case 'cancelled':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Cancelled" />
      ); // GRAY-6
    default:
      return type;
  }
};

export const StatusGiftCardCell = (type: string) => {
  switch (type) {
    case 'active':
      return (
        <Tag style={{ marginRight: 0 }} color="#52c41a">
          Active
        </Tag>
      ); // GRENN-6
    case 'expired':
      return (
        <Tag style={{ marginRight: 0 }} color="#fa8c16">
          Expired
        </Tag>
      ); // ORANGE-6
    case 'empty':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Empty
        </Tag>
      ); // GRAY-6
    case 'rejected':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Rejected
        </Tag>
      ); // GRAY-6
    case 'cancelled':
    case 'pending':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Pending
        </Tag>
      ); // GRAY-6
    default:
      return type;
  }
};

export const CoverCell = (
  name: string,
  entity: { coverPicture: PictureComponentProps }
) => {
  return (
    <div className="cover-cell">
      <div className="cover-cell__thumbnail">
        <PictureComponent {...entity.coverPicture} />
      </div>
      <div className="cover-cell">{name}</div>
    </div>
  );
};

export const CoverCellCustomFunction =
  (f: (entity: { coverPicture: PictureComponentProps }) => ReactNode) =>
  (value: unknown, entity: { coverPicture: PictureComponentProps }) => {
    return (
      <div className="cover-cell">
        <div className="cover-cell__thumbnail">
          <PictureComponent {...entity.coverPicture} />
        </div>
        <div className="cover-cell">{f(entity)}</div>
      </div>
    );
  };

type CoverCellRoomProps = {
  category: string;
  entity?: {
    name?: string | null;
    coverPicture?: PictureView | null;
  };
};

export const CoverCellRoom = ({ category, entity }: CoverCellRoomProps) => {
  const { coverPicture, name } = entity ?? {};

  return (
    <div className="cover-cell-room">
      <div
        className={`cover-cell-room__thumbnail ${
          coverPicture?.name?.includes('emoji')
            ? 'cover-cell-room__thumbnail-emoji'
            : ''
        }`}
      >
        {coverPicture ? (
          <PictureComponentV2
            path={coverPicture.path || ''}
            alt={coverPicture.alt ?? ''}
            width={coverPicture.dimensions?.width ?? 50}
            height={coverPicture.dimensions?.height ?? 50}
          />
        ) : (
          <NoPicture />
        )}
      </div>

      <div className="cover-cell-room__text">
        <div className="cover-cell-room__title">{category}</div>
        {name && <div className="cover-cell-room__subtitle">{name}</div>}
      </div>
    </div>
  );
};

export const TagsCell =
  (tags: { id: number; color?: string; name: string }[]) =>
  (tagIds: number[]) =>
    tagIds
      ? tagIds.map((id) => {
          const tag = tags?.find((t) => t.id === id);

          if (!tag) {
            return null;
          }

          return (
            <Tag key={id} color={tag.color}>
              {tag.name}
            </Tag>
          );
        })
      : null;
